import type {MutableRefObject, RefCallback} from 'react'

export const noop = (..._args: any) => {
  return
}

export const anoop = async (..._args: any) => {
  return
}

export const uuidv4 = () => {
  const hexDigits = '0123456789abcdef'
  let uuid = ''
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      uuid += '-'
    } else if (i === 14) {
      uuid += '4'
    } else if (i === 19) {
      uuid += hexDigits.charAt(Math.floor(Math.random() * 4) + 8)
    } else {
      uuid += hexDigits.charAt(Math.floor(Math.random() * 16))
    }
  }
  return uuid
}

export const randomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

export const randomRGBa = (seed: number = Math.random(), alpha = 1) => {
  const rgb = () => Math.round(Math.abs(Math.sin(seed)) * 255)
  return `rgba(${rgb()},${rgb()},${rgb()},${alpha})`
}

type MutableRefList<T> = Array<RefCallback<T> | MutableRefObject<T> | undefined | null>

export function setRef<T>(val: T, ...refs: MutableRefList<T>): void {
  refs.forEach((ref) => {
    if (typeof ref === 'function') {
      ref(val)
    } else if (ref != null) {
      ref.current = val
    }
  })
}

export function mergeRefs<T>(...refs: MutableRefList<T>): RefCallback<T> {
  return (val: T) => {
    setRef(val, ...refs)
  }
}

/**
 * wrap accepts a range, defined as a min and max.
 * When a third number is provided:
 * If it lies within the range, it is returned.
 * If it lies outside the range, it is wrapped back around:
 */
export const wrap = (min: number, max: number, v: number) => {
  const rangeSize = max - min
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min
}

export const daysFromNow = (date: Date) => {
  const oneDay = 24 * 60 * 60 * 1000
  return Math.floor(Math.abs((Date.now() - date.getTime()) / oneDay))
}

export const truncate = (str: string, chars: number) => {
  if (str.length > chars) {
    return str.slice(0, chars) + '...'
  } else {
    return str
  }
}

export const prettyMimeType = (mimeType: string) => {
  const prettyMimeTypes: Record<string, string> = {
    'image/jpeg': 'Media',
    'application/pdf': 'PDF',
  }
  return prettyMimeTypes[mimeType] ?? mimeType
}

export const date = (
  value: string,
  {compact, longYear, locale}: {compact?: boolean; longYear?: boolean; locale?: string} = {
    compact: false,
    longYear: false,
    locale: 'de-De',
  },
) => {
  if (!value) return ''
  const date = new Date(value)

  const options: Intl.DateTimeFormatOptions = compact
    ? {
      month: '2-digit',
      year: longYear ? 'numeric' : '2-digit',
    }
    : {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour12: false,
    }

  return new Intl.DateTimeFormat(locale, options).format(date)
}

export const shortDate = (value: string) => date(value, {compact: true, longYear: true, locale: 'en-US'})

export const parseNumber = (value?: string | number | null) => {
  if (typeof value !== 'number' && !value) return ''
  const number = Number(value)
  return new Intl.NumberFormat('de-DE').format(number)
}

export const isEven = (number: number) => number % 2 === 0
export const isOdd = (number: number) => number % 2 === 1

export const sleep = (period: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve()
    }, period)
  })
}

export type DurationObject = {
  hours: number
  minutes: number
  seconds: number
}

export const secondsToDurationObject = (total = 0): DurationObject => {
  const values = {
    hours: 0,
    minutes: 0,
    seconds: Math.abs(total),
  }

  // calculate hours
  if (values.seconds >= 3600) {
    values.hours = Math.floor(values.seconds / 3600)
    values.seconds = values.seconds - (values.hours * 3600)
  }

  // calculate minutes
  if (values.seconds >= 60) {
    values.minutes = Math.floor(values.seconds / 60)
    values.seconds = values.seconds - (values.minutes * 60)
  }

  return values
}

export const durationObjectToString = ({hours, minutes, seconds}: DurationObject) => {
  const h = hours > 0
    ? `${hours > 9 ? hours : `0${hours}`}:`
    : ''
  const m = minutes > 9 ? minutes : `0${minutes}`
  const s = seconds > 9 ? seconds : `0${seconds}`
  return `${h}${m}:${s}`
}

export const parseSecondsHumanReadableString = (total = 0) => durationObjectToString(secondsToDurationObject(total))

export const time = (value: number, unit: Intl.RelativeTimeFormatUnit = 'day') => {
  return new Intl.RelativeTimeFormat('de-DE').format(value, unit)
}

export const ucfirst = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export const createRandomHash = () => Math.random().toString(36).substring(7)

export const ignoreErrors = (fn: () => void) => {
  try {
    fn()
  } catch (e) {
    // Nothing to do
  }
}

export const isEmailRegExp = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
export const parseBool = (value: string | null) => (!value ? false : value.toLowerCase() === 'true')

export const objToParams = (paramsObject: any) =>
  Object.entries(paramsObject)
    .map(([k, v]) => `${k}=${v}`)
    .join('&')

export const currency = (value?: string | number | null, currency = 'EUR') => {
  if (!value) return 'k.A.'
  if (isNaN(Number(value))) return 'k.A.'
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Number(value))
}

export const daysBetweenTwoDates = (d1: Date, d2: Date) => {
  const differenceMs = d2.getTime() - d1.getTime()
  return Math.abs(Math.ceil(differenceMs / (1000 * 60 * 60 * 24)))
}

export const countDecimals = (value: number) => {
  if (Math.floor(value) === value) return 0
  return value.toString().split('.')[1].length || 0
}

export const LoremIpsum = 'Lorem ipsum dolor sit amet consectetur adipisicing elit. Rerum unde in delectus eius ea similique tempora distinctio, tempore quidem inventore doloremque, cum maiores maxime? Odio nesciunt impedit culpa est quibusdam!'

export const extractDomain = (urlString: string): string => {
  const url = new URL(urlString.replace('www.', ''))
  const parts = url.hostname.split('.').reverse()
  if (parts.length > 1){
    let sliceAmount = 2
    // there are some edgecases where the suffix is a double ending. These are the known suffixes
    const knownSuffixes = ['co.uk', 'com.au', 'gov.uk', 'ac.uk', 'org.uk', 'edu.au', 'net.au']
    if (parts.length > 2 && knownSuffixes.includes(`${parts[1]}.${parts[0]}`)) {
      sliceAmount = 3
    }
    return parts.slice(0, sliceAmount).reverse().join('.')
  }
  return url.toString()
}

export const removeByValue = (obj: Record<string, string>, value: string) => {
  for (const key in obj) {
    if (obj[key] === value) {
      delete obj[key]
      break
    }
  }
}

export const slashToDash = (value: string) => value.replace(/\//g, '-')

export const isVariableAliasObj = (obj: any) => obj?.type === 'VARIABLE_ALIAS'

export enum DirectionEnum {
  NONE = 'none',
  LEFT = 'left',
  RIGHT = 'right',
}

export const intToDirectionEnum = (num: number) => {
  if (num === 0) return DirectionEnum.NONE
  return num > 0 ? DirectionEnum.RIGHT : DirectionEnum.LEFT
}

const digitRegex = /^[0-9]$/
export const isDigit = (char: string) => digitRegex.test(char)

export const removeNullValues = <T extends Record<string, any>>(obj: T): {[K in keyof T]: NonNullable<T[K]>} => {
  if (obj === null || typeof obj !== 'object') {
    return obj as NonNullable<T>
  }

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, value]) => value != null)
      .map(([key, value]) => [
        key,
        typeof value === 'object' ? removeNullValues(value) : value,
      ]),
  ) as {[K in keyof T]: NonNullable<T[K]>}
}

export type FixedArray<TType, TLength extends number> = TType[] & {length: TLength}